import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, Menu, LayoutGrid, FolderOpen, Home, FolderKanban, IdCard, Music2, BookOpen, FileBarChart2, HelpCircle, Megaphone, CreditCard, ShieldCheck, Settings2, Building2, Hammer, Brain, ScrollText, Archive, Users, Paperclip, BookMarked, Workflow, MessageSquare, NotebookPen, CalendarClock, HeartHandshake, Grid3x3, GraduationCap, ListTodo, Map, Network, Gauge, BarChart3, Flag, AlertTriangle, CheckSquare, GanttChartSquare, ChevronDown, UserCheck, Sparkles, Radar } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AutoWelcomeTour } from "@/components/WelcomeTour";
import { TrialBadge } from "@/components/TrialBadge";
import MaestroOnboarding from "@/components/MaestroOnboarding";
import MaestroGuide from "@/components/MaestroGuide";

const LAYERS = [
  { key: "project", label: "Project", icon: FolderOpen, active: "bg-blue-600 text-white", home: "/home" },
  { key: "portfolio", label: "Portfolio", icon: LayoutGrid, active: "bg-violet-600 text-white", home: "/portfolio" },
  { key: "pmo", label: "PMO", icon: Building2, active: "bg-emerald-600 text-white", home: "/pmo" },
];

function routeLayer(path) {
  if (path.startsWith("/pmo")) return "pmo";
  if (path.startsWith("/portfolio") || path.startsWith("/audit") || path.startsWith("/assignments")) return "portfolio";
  if (path.startsWith("/home") || path.startsWith("/projects")) return "project";
  return null;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [meetMaestro, setMeetMaestro] = useState(false);
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const canPMO = ["supervisor", "admin"].includes(user?.role);

  const fromRoute = routeLayer(loc.pathname);
  const layer = fromRoute || localStorage.getItem("active-layer") || "project";
  useEffect(() => { if (fromRoute) localStorage.setItem("active-layer", fromRoute); }, [fromRoute]);

  const shared = [
    { to: "/welcome", label: "Welcome", icon: Sparkles },
    { to: "/guide", label: "Get Started", icon: HelpCircle },
  ];
  const MENUS = {
    project: [
      { to: "/home", label: "My Dashboard", short: "My Dashboard", icon: Home, accent: true },
      { label: "Plan", icon: FolderKanban, items: [
        { to: "/dashboard", label: "Projects", sub: "All active projects", icon: FolderKanban },
        { to: "/dashboard?new=1", label: "Build a project", sub: "From scratch, template or AI", icon: Hammer },
        { to: "/go/plan", label: "WBS Plan", sub: "Spreadsheet work breakdown", icon: ListTodo },
        { to: "/go/ioflow", label: "IO Flow", sub: "Input → Output phase map", icon: Workflow },
        { to: "/go/roadmap", label: "Roadmap", sub: "Swimlane timeline", icon: Map },
        { to: "/go/timeline", label: "Gantt", sub: "Bars with dependencies", icon: GanttChartSquare },
        { to: "/go/map", label: "Visual Map", sub: "Network flow diagram", icon: Network },
      ] },
      { label: "Track", icon: Gauge, items: [
        { to: "/status-report", label: "Status Report", sub: "Build & share read-outs", icon: FileBarChart2 },
        { to: "/go/reports", label: "Reports", sub: "KPIs & charts", icon: BarChart3 },
        { to: "/go/baseline", label: "Baseline", sub: "Slipped vs improved", icon: Flag },
        { to: "/go/risks", label: "Risks & Issues", sub: "RAID register", icon: AlertTriangle },
        { to: "/go/actions", label: "Action Items", sub: "Owners & due dates", icon: CheckSquare },
        { to: "/go/radar", label: "On the Radar", sub: "Parked & on-hold items", icon: Radar },
        { to: "/go/audit", label: "Audit", sub: "PM standards & hygiene score", icon: ShieldCheck },
      ] },
      { label: "People", icon: Users, items: [
        { to: "/cards", label: "Cards", sub: "Per-person open items", icon: IdCard },
        { to: "/go/resources", label: "Resources", sub: "Registry & allocation", icon: Users },
        { to: "/go/stakeholders", label: "Stakeholders", sub: "Engagement & comm plan", icon: HeartHandshake },
        { to: "/go/teams", label: "Teams", sub: "RACI matrix", icon: Grid3x3 },
      ] },
      { label: "Communicate", icon: MessageSquare, items: [
        { to: "/spaces", label: "Orchestra Pit", sub: "Team chat", icon: Music2 },
        { to: "/go/notebook", label: "Minutes", sub: "Meeting notebook & AI minutes", icon: NotebookPen },
        { to: "/go/meeting", label: "Agendas", sub: "AI meeting prep", icon: CalendarClock },
      ] },
      { to: "/library", label: "Library", icon: BookOpen },
      { to: "/lessons", label: "Lessons Learned", short: "Lessons", icon: GraduationCap },
      ...shared,
    ],
    portfolio: [
      { to: "/portfolio", label: "Portfolio Dashboard", short: "Dashboard", icon: LayoutGrid },
      { to: "/dashboard", label: "Projects", icon: FolderKanban },
      { to: "/cards", label: "Cards", icon: IdCard },
      { to: "/spaces", label: "Orchestra Pit", icon: Music2 },
      { to: "/library", label: "Library", icon: BookOpen },
      { to: "/status-report", label: "Status Report", icon: FileBarChart2 },
      ...(canPMO ? [{ to: "/assignments", label: "Assignments", icon: UserCheck }] : []),
      ...(canPMO ? [{ to: "/audit", label: "Audit", icon: ShieldCheck }] : []),
      ...shared,
    ],
    pmo: [
      { to: "/pmo", label: "PMO Dashboard", short: "Dashboard", icon: Building2 },
      { label: "Build", icon: Hammer, items: [
        { to: "/dashboard?new=1", label: "Build a Project", sub: "From scratch, template or AI", icon: Hammer },
        { to: "/pmo/ai-plan", label: "AI Plan", sub: "Generate a best-practice plan with AI", icon: Brain },
      ] },
      ...(canPMO ? [{ label: "Intelligence", icon: Gauge, items: [
        { to: "/pmo/exec-summary", label: "AI Executive Summary", sub: "Leadership-ready portfolio narrative", icon: ScrollText },
        { to: "/pmo/registry", label: "Resource Registry", sub: "Contacts, influence & velocity", icon: Users },
      ] }] : []),
      { label: "Knowledge", icon: Archive, items: [
        { to: "/pmo/archived", label: "Archived Projects", sub: "Searchable completed work", icon: Archive },
        { to: "/pmo/artifacts", label: "Artifacts", sub: "Documents across all projects", icon: Paperclip },
        { to: "/pmo/process", label: "Process", sub: "Documented process flows", icon: Workflow },
      ] },
      { label: "Library", icon: BookOpen, items: [
        { to: "/library", label: "Library Home", sub: "Everything reusable in one place", icon: BookOpen },
        { to: "/library?tab=templates", label: "Project Templates", sub: "Start-ready project plans", icon: BookMarked },
        { to: "/library?tab=items", label: "Tasks & Milestones", sub: "Reusable building blocks", icon: ListTodo },
        { to: "/library?tab=resources", label: "Resource Library", sub: "Saved people & roles", icon: Users },
      ] },
      ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: Settings2 }] : []),
      ...shared,
    ],
  };
  const links = MENUS[layer] || MENUS.project;
  const visibleLayers = LAYERS;

  const isActive = (to) => {
    const [base, qs] = to.split("?");
    if (base.startsWith("/go/")) {
      if (!loc.pathname.startsWith("/projects/")) return false;
      const cur = new URLSearchParams(loc.search).get("tab") || "plan";
      return cur === base.slice(4);
    }
    const wantTab = qs ? new URLSearchParams(qs).get("tab") : null;
    if (base === "/library") {
      if (!loc.pathname.startsWith("/library")) return false;
      const cur = new URLSearchParams(loc.search).get("tab");
      return wantTab ? cur === wantTab : (!cur || cur === "items");
    }
    if (base === "/dashboard" || base === "/pmo") return loc.pathname === base;
    return loc.pathname.startsWith(base);
  };
  const kebab = (s) => s.toLowerCase().replace(/\s+/g, "-");
  const switchLayer = (l) => { localStorage.setItem("active-layer", l.key); setMobileOpen(false); nav(l.home); };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 xl:gap-4 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden shrink-0" data-testid="mobile-menu-button" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 overflow-y-auto" data-testid="mobile-menu-sheet">
                <SheetHeader className="px-5 pt-5 pb-3 text-left">
                  <SheetTitle className="font-display text-lg font-extrabold tracking-tight">PM Orchestrator</SheetTitle>
                </SheetHeader>
                <div className="px-3 pb-24">
                  {visibleLayers.map((l) => (
                    <div key={l.key} className="mb-3">
                      <button onClick={() => switchLayer(l)}
                        className={`w-full text-left px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 ${layer === l.key ? l.active : "text-muted-foreground hover:bg-secondary/60"}`}
                        data-testid={`mobile-layer-${l.key}`}>
                        <l.icon className="h-3.5 w-3.5" /> {l.label} layer
                      </button>
                      {layer === l.key && (
                        <div className="mt-1 flex flex-col gap-0.5">
                          {links.map((k) => k.items ? (
                            <div key={k.label}>
                              <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                                <k.icon className="h-3 w-3" /> {k.label}
                              </p>
                              {k.items.map((s) => (
                                <button key={s.to} onClick={() => { setMobileOpen(false); nav(s.to); }}
                                  className={`w-full text-left pl-8 pr-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2.5 ${isActive(s.to) ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
                                  data-testid={`mobile-nav-${kebab(s.label)}`}>
                                  <s.icon className="h-4 w-4" />
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <button key={k.to} onClick={() => { setMobileOpen(false); nav(k.to); }}
                              className={k.accent
                                ? `w-full text-left px-3 py-2 rounded-md text-sm font-semibold transition-colors flex items-center gap-2.5 ${isActive(k.to) ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700"}`
                                : `w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2.5 ${isActive(k.to) ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
                              data-testid={`mobile-nav-${kebab(k.label)}`}>
                              <k.icon className="h-4 w-4" />
                              {k.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="border-t border-border px-5 py-4 flex items-center gap-3 absolute bottom-0 left-0 right-0 bg-background">
                  <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">{initials}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate" data-testid="mobile-menu-user-name">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => { setMobileOpen(false); logout(); }} data-testid="mobile-logout-button" title="Log out">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <button onClick={() => nav("/dashboard")} className="flex items-center gap-2 group shrink-0" data-testid="brand-home-button">
              <span className="font-display text-xl font-extrabold tracking-tight group-hover:text-primary transition-colors duration-300 whitespace-nowrap">PM Orchestrator</span>
            </button>
            <div className="hidden md:flex items-center rounded-full border border-border overflow-hidden shrink-0" data-testid="view-toggle">
              {visibleLayers.map((l) => (
                <button key={l.key} onClick={() => switchLayer(l)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${layer === l.key ? l.active : "text-muted-foreground hover:bg-secondary"}`}
                  data-testid={`view-toggle-${l.key}`}>
                  <l.icon className="h-3 w-3" /> {l.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <TrialBadge />
            <Button variant="ghost" size="sm" className="gap-1.5 text-primary hover:text-primary" onClick={() => setMeetMaestro(true)} data-testid="nav-meet-maestro" title="Meet Maestro — replay the guided intro & tour">
              <Music2 className="h-4 w-4" /> <span className="hidden lg:inline text-xs font-bold">Meet Maestro</span>
            </Button>
            <Button variant="ghost" size="icon" onClick={() => nav("/guide")} data-testid="nav-guide" title="Getting started guide">
              <HelpCircle className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => nav("/whats-new")} data-testid="nav-whats-new" title="What's new — latest improvements">
              <Megaphone className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => nav("/billing")} data-testid="nav-billing" title="Billing — your plan & invoices">
              <CreditCard className="h-4 w-4" />
            </Button>
            <span className="sr-only" data-testid="current-user-name">{user?.name}</span>
            <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold" title={user?.name}>{initials}</div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" title="Log out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="hidden md:block pb-2.5">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/60 mb-0.5 pl-1" data-testid="program-menu-label">Program Level Menu</p>
            <nav className="inline-flex flex-wrap items-center gap-1 rounded-xl border border-border bg-background p-1 shadow-sm" data-testid={`layer-nav-${layer}`}>
              {links.map((k) => {
                if (k.items) {
                  const activeChild = k.items.find((s) => isActive(s.to));
                  return (
                    <DropdownMenu key={`${layer}-${k.label}`}>
                      <DropdownMenuTrigger asChild>
                        <button
                          title={k.label}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[13px] font-semibold transition-colors whitespace-nowrap ${activeChild ? "bg-secondary/70 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
                          data-testid={`nav-group-${kebab(k.label)}`}
                        >
                          {k.label}
                          {activeChild && <span className="text-blue-600 pl-0.5">· {activeChild.label}</span>}
                          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-64">
                        {k.items.map((s) => (
                          <DropdownMenuItem key={s.to} onClick={() => nav(s.to)} className={`gap-2.5 cursor-pointer ${isActive(s.to) ? "bg-secondary" : ""}`} data-testid={`nav-${kebab(s.label)}`}>
                            <s.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-medium">{s.label}</span>
                              {s.sub && <span className="block text-[11px] text-muted-foreground">{s.sub}</span>}
                            </span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                return (
                  <button
                    key={`${layer}-${k.to}`}
                    onClick={() => nav(k.to)}
                    title={k.label}
                    className={k.accent
                      ? `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold transition-colors whitespace-nowrap ${isActive(k.to) ? "bg-blue-600 text-white shadow-sm" : "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"}`
                      : `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[13px] font-semibold transition-colors whitespace-nowrap ${isActive(k.to) ? "text-blue-700 bg-blue-50" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
                    data-testid={`nav-${kebab(k.label)}`}
                  >
                    <k.icon className="h-4 w-4" />
                    {k.short || k.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">{children}</main>
      <AutoWelcomeTour />
      <MaestroGuide />
      {meetMaestro && <MaestroOnboarding replay firstName={(user?.name || "").split(" ")[0]} onClose={() => setMeetMaestro(false)} />}
    </div>
  );
}
